import { useGetStudentTimeline } from '@actions';
import { LoadingSpinner } from '@components/LoadingSpinner/LoadingSpinner';
import {
  TimelineCategory,
  TimelineEventDTO,
  TimelineTone,
} from '@shared/apis/studentInsights.apis';
import { mergeClassNames } from 'apps/agora/src/utils/helpers';
import moment from 'moment';
import { useContext, useMemo, useState } from 'react';
import DetailsModalSection from '../../../DetailsModalSection';
import { StudentDetailsPageContext } from '../../StudentDetailsPage';

const PAGE_SIZE = 100;

const CATEGORY_LABELS: Record<TimelineCategory, string> = {
  meeting: 'Meetings',
  feedback: 'Feedback',
  enrollment: 'Enrollments',
  homework: 'Homework',
  email: 'Emails',
  payment: 'Payments',
  university: 'Universities',
  nps: 'NPS',
  flag: 'Flags',
  admin: 'Admin changes',
  account: 'Account',
  report: 'Reports',
  hubspot: 'HubSpot',
};

const TONE_DOTS: Record<TimelineTone, string> = {
  positive: 'bg-emerald-400',
  negative: 'bg-red-400',
  warning: 'bg-amber-400',
  neutral: 'bg-customGrey',
};

const TimelineTab = () => {
  const { student, onSessionModalOpen, onEnrollmentModalOpen } =
    useContext(StudentDetailsPageContext);
  const { data: events, isLoading } = useGetStudentTimeline(student?._id);
  const [hidden, setHidden] = useState<TimelineCategory[]>(['admin']);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const counts = useMemo(() => {
    const result = new Map<TimelineCategory, number>();
    (events ?? []).forEach((event) =>
      result.set(event.category, (result.get(event.category) ?? 0) + 1)
    );
    return result;
  }, [events]);

  const filtered = useMemo(
    () => (events ?? []).filter((event) => !hidden.includes(event.category)),
    [events, hidden]
  );

  const groups = useMemo(() => {
    const result: { label: string; items: TimelineEventDTO[] }[] = [];

    filtered.slice(0, visibleCount).forEach((event) => {
      const label = moment(event.at).format('MMMM YYYY');
      const last = result[result.length - 1];
      if (last?.label === label) last.items.push(event);
      else result.push({ label, items: [event] });
    });

    return result;
  }, [filtered, visibleCount]);

  const toggle = (category: TimelineCategory) => {
    setHidden((previous) =>
      previous.includes(category)
        ? previous.filter((item) => item !== category)
        : [...previous, category]
    );
    setVisibleCount(PAGE_SIZE);
  };

  const open = (event: TimelineEventDTO) => {
    if (event.sessionId) onSessionModalOpen(event.sessionId);
    else if (event.enrollmentId) onEnrollmentModalOpen(event.enrollmentId);
  };

  return (
    <DetailsModalSection sectionTitle="Activity Timeline" className="flex flex-col gap-4">
      {isLoading ? (
        <LoadingSpinner className="mx-auto" />
      ) : !events?.length ? (
        <p className="text-sm text-customGrey">No activity recorded for this student yet.</p>
      ) : (
        <>
          <div className="flex flex-wrap gap-2">
            {(Object.keys(CATEGORY_LABELS) as TimelineCategory[])
              .filter((category) => counts.get(category))
              .map((category) => (
                <button
                  key={category}
                  type="button"
                  className={mergeClassNames(
                    'rounded-full border px-3 py-1 text-xs transition-colors',
                    hidden.includes(category)
                      ? 'border-white/10 text-customGrey'
                      : 'border-customPrimary bg-customPrimary/10 text-white'
                  )}
                  onClick={() => toggle(category)}
                >
                  {CATEGORY_LABELS[category]} ({counts.get(category)})
                </button>
              ))}
          </div>

          {groups.map((group) => (
            <div key={group.label} className="flex flex-col gap-1">
              <p className="text-xs font-bold uppercase tracking-wide text-customGrey">
                {group.label}
              </p>
              <ul className="flex flex-col border-l border-white/10">
                {group.items.map((event) => {
                  const isClickable = !!(event.sessionId || event.enrollmentId);
                  const isFuture = moment(event.at).isAfter(moment());

                  return (
                    <li key={event.id} className="relative pl-5">
                      <span
                        className={mergeClassNames(
                          'absolute -left-[5px] top-3 h-2.5 w-2.5 rounded-full',
                          TONE_DOTS[event.tone]
                        )}
                      />
                      <button
                        type="button"
                        disabled={!isClickable}
                        className={mergeClassNames(
                          'flex w-full flex-col items-start rounded-lg px-2 py-1.5 text-left',
                          isClickable ? 'hover:bg-surfaceHover' : 'cursor-default'
                        )}
                        onClick={() => open(event)}
                      >
                        <span className="flex flex-wrap items-center gap-2 text-xs text-customGrey">
                          {moment(event.at).format('DD MMM YYYY, HH:mm')}
                          <span className="rounded-full bg-white/10 px-2 py-0.5">
                            {CATEGORY_LABELS[event.category]}
                          </span>
                          {isFuture ? (
                            <span className="rounded-full bg-customPrimary/10 px-2 py-0.5 text-customPrimary">
                              Upcoming
                            </span>
                          ) : null}
                        </span>
                        <span className="text-sm text-white">{event.title}</span>
                        {event.detail ? (
                          <span className="whitespace-pre-line text-xs text-customGrey">
                            {event.detail}
                          </span>
                        ) : null}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}

          {filtered.length > visibleCount ? (
            <button
              type="button"
              className="self-start text-sm text-customPrimary"
              onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
            >
              Show older activity ({filtered.length - visibleCount} more)
            </button>
          ) : null}
        </>
      )}
    </DetailsModalSection>
  );
};

export default TimelineTab;
